import React, { useState, useMemo } from 'react';

// 'YYYY-MM-DD' en hora local (hoy + offset días)
const ymdLocal = (offsetDias = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fechaYMD = (f) => String(f || '').substring(0, 10);

const formatearFecha = (f) => {
  const [y, m, d] = fechaYMD(f).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '-';
};

const TablasCitas = ({
  citas,
  onEditarCita,
  onReagendarCita,
  onCambiarEstado,
  onVerComprobante,
  onCancelarCita,
  loading = false
}) => {
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);

  const itemsPorPagina = 10;

  const hoyYMD = ymdLocal(0);
  const mananaYMD = ymdLocal(1);
  const atajoActivo =
    filtroFechaInicio === hoyYMD && filtroFechaFin === hoyYMD ? 'hoy'
    : filtroFechaInicio === mananaYMD && filtroFechaFin === mananaYMD ? 'manana'
    : filtroFechaInicio === hoyYMD && !filtroFechaFin ? 'proximas'
    : !filtroFechaInicio && !filtroFechaFin ? 'todas'
    : null;

  const aplicarAtajo = (atajo) => {
    if (atajo === 'hoy') { setFiltroFechaInicio(hoyYMD); setFiltroFechaFin(hoyYMD); }
    else if (atajo === 'manana') { setFiltroFechaInicio(mananaYMD); setFiltroFechaFin(mananaYMD); }
    else if (atajo === 'proximas') { setFiltroFechaInicio(hoyYMD); setFiltroFechaFin(''); }
    else { setFiltroFechaInicio(''); setFiltroFechaFin(''); }
    setPaginaActual(1);
  };

  const citasFiltradas = useMemo(() => {
    let resultado = citas;

    // Filtro por estado
    if (filtroEstado !== 'todas') {
      resultado = resultado.filter(c => c.estado === filtroEstado);
    }

    // Filtro por rango de fechas (fecha_cita llega como 'YYYY-MM-DD': comparar como texto
    // evita el desfase de zona horaria de new Date('YYYY-MM-DD'), que se interpreta en UTC)
    if (filtroFechaInicio) {
      resultado = resultado.filter(c => fechaYMD(c.fecha_cita) >= filtroFechaInicio);
    }
    if (filtroFechaFin) {
      resultado = resultado.filter(c => fechaYMD(c.fecha_cita) <= filtroFechaFin);
    }

    // Un solo día seleccionado: ordenar por hora ascendente (agenda del día)
    if (filtroFechaInicio && filtroFechaInicio === filtroFechaFin) {
      resultado = [...resultado].sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || ''));
    }

    // Búsqueda por cliente o servicio
    if (busqueda.trim()) {
      const termino = busqueda.toLowerCase();
      resultado = resultado.filter(c =>
        c.cliente_nombre?.toLowerCase().includes(termino) ||
        c.servicio_nombre?.toLowerCase().includes(termino)
      );
    }

    return resultado;
  }, [citas, filtroEstado, filtroFechaInicio, filtroFechaFin, busqueda]);

  const totalPaginas = Math.ceil(citasFiltradas.length / itemsPorPagina);
  const citasPaginadas = citasFiltradas.slice(
    (paginaActual - 1) * itemsPorPagina,
    paginaActual * itemsPorPagina
  );

  const estadoColor = {
    pendiente: '#f59e0b',
    confirmada: '#10b981',
    completada: '#3b82f6',
    cancelada: '#ef4444',
    no_asistio: '#6b7280',
    pendiente_pago: '#f97316'
  };

  const estadoTexto = {
    pendiente: 'Pendiente',
    confirmada: 'Confirmada',
    completada: 'Completada',
    cancelada: 'Cancelada',
    no_asistio: 'No asistió',
    pendiente_pago: 'Pendiente pago'
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Atajos de fecha */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'hoy', label: 'Hoy' },
          { id: 'manana', label: 'Mañana' },
          { id: 'proximas', label: 'Próximas' },
          { id: 'todas', label: 'Todas' },
        ].map(a => (
          <button
            key={a.id}
            onClick={() => aplicarAtajo(a.id)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '999px',
              border: `1px solid ${atajoActivo === a.id ? '#3b82f6' : '#d1d5db'}`,
              backgroundColor: atajoActivo === a.id ? '#3b82f6' : '#ffffff',
              color: atajoActivo === a.id ? '#ffffff' : '#374151',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            {a.label}
          </button>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', fontSize: '0.875rem', color: '#6b7280', fontWeight: 600 }}>
          Día:
          <input
            type="date"
            value={filtroFechaInicio && filtroFechaInicio === filtroFechaFin ? filtroFechaInicio : ''}
            onChange={(e) => {
              setFiltroFechaInicio(e.target.value);
              setFiltroFechaFin(e.target.value);
              setPaginaActual(1);
            }}
            style={{ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
          />
        </label>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <input
          type="text"
          placeholder="Buscar cliente o servicio..."
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}
        />

        <select
          value={filtroEstado}
          onChange={(e) => {
            setFiltroEstado(e.target.value);
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}
        >
          <option value="todas">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="confirmada">Confirmada</option>
          <option value="completada">Completada</option>
          <option value="cancelada">Cancelada</option>
          <option value="no_asistio">No asistió</option>
          <option value="pendiente_pago">Pendiente pago</option>
        </select>

        <input
          type="date"
          placeholder="Desde..."
          title="Desde"
          value={filtroFechaInicio}
          onChange={(e) => {
            setFiltroFechaInicio(e.target.value);
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}
        />

        <input
          type="date"
          placeholder="Hasta..."
          title="Hasta"
          value={filtroFechaFin}
          onChange={(e) => {
            setFiltroFechaFin(e.target.value);
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}
        />

        <button
          onClick={() => {
            setBusqueda('');
            setFiltroEstado('todas');
            setFiltroFechaInicio('');
            setFiltroFechaFin('');
            setPaginaActual(1);
          }}
          style={{
            padding: '0.75rem',
            backgroundColor: '#e5e7eb',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#d1d5db'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#e5e7eb'}
        >
          Limpiar filtros
        </button>
      </div>

      {/* Tabla */}
      <div style={{
        overflowX: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.95rem'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151' }}>Cliente</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151' }}>Servicio</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151' }}>Profesional</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151' }}>Fecha</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151' }}>Hora</th>
              <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151' }}>Estado</th>
              <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {citasPaginadas.map((cita, idx) => (
              <tr
                key={cita.id || idx}
                style={{
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#f9fafb'}
              >
                <td style={{ padding: '1rem', color: '#374151', fontWeight: '500' }}>
                  {cita.cliente_nombre || '-'}
                </td>
                <td style={{ padding: '1rem', color: '#6b7280' }}>
                  {cita.servicio_nombre || '-'}
                </td>
                <td style={{ padding: '1rem', color: '#6b7280' }}>
                  {cita.profesional_nombre || '-'}
                </td>
                <td style={{ padding: '1rem', color: '#6b7280' }}>
                  {formatearFecha(cita.fecha_cita)}
                </td>
                <td style={{ padding: '1rem', color: '#6b7280', fontWeight: '600' }}>
                  {cita.hora_inicio?.substring(0, 5)}
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.4rem 0.8rem',
                    backgroundColor: estadoColor[cita.estado] + '22',
                    color: estadoColor[cita.estado],
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '0.85rem'
                  }}>
                    {estadoTexto[cita.estado]}
                  </span>
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => onEditarCita(cita)}
                      title="Editar"
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.4rem 0.8rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.opacity = '0.8'}
                      onMouseOut={(e) => e.target.style.opacity = '1'}
                    >
                      ✏️
                    </button>

                    <button
                      onClick={() => onReagendarCita(cita)}
                      title="Reagendar"
                      style={{
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.4rem 0.8rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.opacity = '0.8'}
                      onMouseOut={(e) => e.target.style.opacity = '1'}
                    >
                      🔄
                    </button>

                    <button
                      onClick={() => onCambiarEstado(cita)}
                      title="Cambiar estado"
                      style={{
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.4rem 0.8rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.opacity = '0.8'}
                      onMouseOut={(e) => e.target.style.opacity = '1'}
                    >
                      ✓
                    </button>

                    {cita.comprobante && (
                      <button
                        onClick={() => onVerComprobante(cita.comprobante)}
                        title="Ver comprobante"
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.4rem 0.8rem',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.opacity = '0.8'}
                        onMouseOut={(e) => e.target.style.opacity = '1'}
                      >
                        🧾
                      </button>
                    )}

                    <button
                      onClick={() => onCancelarCita(cita)}
                      title="Cancelar"
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.4rem 0.8rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.opacity = '0.8'}
                      onMouseOut={(e) => e.target.style.opacity = '1'}
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {citasFiltradas.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          No hay citas que coincidan con los filtros
        </div>
      )}

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <button
            onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
            disabled={paginaActual === 1}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            ← Anterior
          </button>

          {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(pagina => (
            <button
              key={pagina}
              onClick={() => setPaginaActual(pagina)}
              style={{
                padding: '0.5rem 0.75rem',
                border: pagina === paginaActual ? '2px solid #3b82f6' : '1px solid #d1d5db',
                backgroundColor: pagina === paginaActual ? '#dbeafe' : 'white',
                color: pagina === paginaActual ? '#3b82f6' : '#6b7280',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: pagina === paginaActual ? '700' : '600'
              }}
            >
              {pagina}
            </button>
          ))}

          <button
            onClick={() => setPaginaActual(Math.min(totalPaginas, paginaActual + 1))}
            disabled={paginaActual === totalPaginas}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Siguiente →
          </button>
        </div>
      )}

      {/* Estadísticas */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        backgroundColor: '#f3f4f6',
        borderRadius: '8px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.9rem'
      }}>
        Mostrando {citasPaginadas.length} de {citasFiltradas.length} citas
        {citasFiltradas.length < citas.length && ` (${citas.length - citasFiltradas.length} filtradas)`}
      </div>
    </div>
  );
};

export default TablasCitas;
